import {
  ComponentRendering,
  Field,
  ImageField,
  Item,
  LinkField,
  NextImage,
  Placeholder,
  Text,
  TextField,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'lib/component-props';
import { useMemo } from 'react';
import useHelpers from 'src/hooks/helpers';

import AbrdnButton from 'components/Generic/Buttons/AbrdnButton';
import classNames from 'classnames';
import { useSitecoreContext } from 'hooks/useSitecoreContext';
import ScRichText from 'components/Generic/SitecoreWrappers/RichText';
import AbrdnPlaceholders from 'components/Generic/SitecoreWrappers/AbrdnPlaceholders';

type TextBlockItem = Item & {
  fields: {
    icon: ImageField;
    description: Field<string>;
    title: TextField;
    cta: LinkField;
  };
};

type Fields = {
  title: TextField;
  items: Array<TextBlockItem>;
};

type RenderingParams = {
  linkType: 'Primary' | 'Secondary' | 'Tertiary';
};

type TextBlocksProps = ComponentProps & {
  fields: Fields;
  rendering: ComponentRendering & { params: RenderingParams };
};

const TextBlocks = ({ fields, rendering, params }: TextBlocksProps): JSX.Element => {
  const haveTitleValue = fields?.title?.value;
  const headingType = params.headingType || 'h2';
  const { sitecoreContext } = useSitecoreContext();
  const isExperienceEditor = sitecoreContext.pageEditing;
  const { anchorId, backgroundClass } = useHelpers(rendering);

  const haveAside = useMemo(() => {
    const { placeholders } = rendering;
    return placeholders && placeholders['abrdn-text-blocks-aside']?.length;
  }, [rendering]);

  const haveInsidePlaceholders = useMemo(() => {
    const { placeholders } = rendering;
    return placeholders && placeholders['abrdn-list-0']?.length;
  }, [rendering]);

  const buttonType = useMemo(() => {
    const { linkType } = rendering.params;
    const buttonTypesMap = {
      Primary: 'primary',
      Secondary: 'secondary',
      Tertiary: 'textLink',
    };
    return buttonTypesMap[linkType] || 'textLink';
  }, [rendering.params]);

  const hasPlaceholders = useMemo(() => {
    const { placeholders } = rendering;
    return (
      placeholders &&
      Object.keys(placeholders)
        .map((key) => {
          return placeholders[key];
        })
        .flat(Infinity).length
    );
  }, [rendering]);

  const getIcon = (textBlock: TextBlockItem) => {
    return textBlock?.fields?.icon?.value?.src;
  };

  return (
    <section
      id={anchorId}
      data-e2e-component-uid={rendering.uid}
      className={`default-section-padding text-blocks link-change ${backgroundClass}`}
    >
      <div className="container text-blocks">
        <div className="row">
          <div className={haveAside ? 'col-12 col-xl-8 mb-4 mb-xl-0' : 'col-12'}>
            {(isExperienceEditor || haveTitleValue) && (
              <div
                className={`row ${
                  hasPlaceholders || (fields.items && fields.items.length) ? 'mb-5' : ''
                }`}
              >
                <div className={`col col-xl-8 col-md-10 ${haveAside ? 'col-12' : ''}`}>
                  <Text tag={headingType} className="background-to-change" field={fields.title} />
                </div>
              </div>
            )}

            {(hasPlaceholders || (fields.items && fields.items.length !== 0)) && (
              <div className="row d-flex">
                {fields.items.map((textBlock, index) => (
                  <div
                    key={index}
                    className={classNames(
                      'col-12 col-md mb-md-0 d-flex flex-column align-items-start',
                      {
                        'col-xl-8 col-md-10': fields.items.length === 1 && !haveAside,
                        'col-md-6 col-xl mb-xl-0': fields.items.length > 3 && !haveAside,
                        'mb-md-5': index <= 1 && fields.items.length > 3 && !haveAside,
                        'mb-5': index !== fields.items.length - 1,
                        'col-12': haveAside,
                      }
                    )}
                  >
                    {(isExperienceEditor || getIcon(textBlock)) && (
                      <div className="mb-2 mb-md-4 image-wrapper">
                        <NextImage
                          field={textBlock.fields.icon}
                          className="image background-to-change"
                          width="100"
                          height="64"
                        />
                      </div>
                    )}
                    {(isExperienceEditor ||
                      (textBlock.fields &&
                        textBlock.fields.title &&
                        textBlock.fields.title.value)) && (
                      <Text
                        tag="h5"
                        className="mb-3 background-to-change"
                        field={textBlock.fields.title}
                      />
                    )}
                    {(isExperienceEditor ||
                      (textBlock.fields &&
                        textBlock.fields.description &&
                        textBlock.fields.description.value)) && (
                      <ScRichText
                        fields={{ ...textBlock.fields, value: textBlock.fields.description.value }}
                        className="contentDescription body-default quote-description text-grey background-to-change mb-0 w-100 link-change"
                      />
                    )}
                    <AbrdnPlaceholders
                      rendering={rendering}
                      className="mt-3"
                      name={`abrdn-list-${index}`}
                    />

                    {(isExperienceEditor ||
                      (textBlock.fields &&
                        textBlock.fields.cta &&
                        textBlock.fields.cta.value &&
                        textBlock.fields.cta.value.text)) && (
                      <>
                        <AbrdnButton
                          buttonType={buttonType}
                          linkField={textBlock.fields.cta}
                          className={!haveInsidePlaceholders ? 'mt-2 mt-md-3' : ''}
                        />
                      </>
                    )}
                  </div>
                ))}
                <div>
                  <Placeholder name="abrdn-text-blocks" rendering={rendering} />
                </div>
              </div>
            )}
          </div>
          <AbrdnPlaceholders
            rendering={rendering}
            className="col-12 col-xl-4"
            name="abrdn-text-blocks-aside"
            singleTile={true}
            hardcodedBg="background-blue-grey"
          />
        </div>
      </div>
    </section>
  );
};

export default withDatasourceCheck()<TextBlocksProps>(TextBlocks);
